import React, { useState } from 'react';
import { Row, Col, Table, Alert, Badge, Button, Card, ProgressBar, Modal } from 'react-bootstrap';
import { FaCheck, FaExclamationTriangle, FaDownload, FaPlay, FaSpinner, FaTimes, FaArrowRight } from 'react-icons/fa';

const ConfirmationStep = ({ workflowData, setWorkflowData, onComplete }) => {
  const [executing, setExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [currentOperation, setCurrentOperation] = useState('');

  // Calculate summary data
  const getSummaryData = () => {
    let studentsToProcess = [];
    
    if (workflowData.populationMethod === 'new') {
      studentsToProcess = (workflowData.newStudents || []).filter(s => s.isValid);
    } else if (workflowData.populationMethod === 'simple') {
      // For simple transitions, we need to get students from mapped source classes
      // This would be calculated based on class mappings and source class students
      const classMappings = workflowData.classMappings || {};
      studentsToProcess = Object.keys(classMappings); // Placeholder - would need actual student data
    } else {
      studentsToProcess = workflowData.selectedStudents || [];
    }

    const decisions = workflowData.studentDecisions || {};
    const summary = {
      totalClasses: workflowData.createdClasses.length,
      totalStudents: studentsToProcess.length,
      moveCount: 0,
      repeatCount: 0,
      removeCount: 0,
      assignCount: 0,
      simpleMappingCount: 0
    };

    if (workflowData.populationMethod === 'simple') {
      summary.simpleMappingCount = Object.keys(workflowData.classMappings || {}).length;
      summary.moveCount = summary.totalStudents; // All students in simple mode are moves
    } else {
      studentsToProcess.forEach(student => {
        const decision = decisions[student.id];
        if (decision) {
          if (workflowData.populationMethod === 'new') {
            summary.assignCount++;
          } else {
            switch (decision.action) {
              case 'move':
                summary.moveCount++;
                break;
              case 'repeat':
                summary.repeatCount++;
                break;
              case 'remove':
                summary.removeCount++;
                break;
            }
          }
        }
      });
    }

    return summary;
  };

  const getStudentsByAction = () => {
    let studentsToProcess = [];
    
    if (workflowData.populationMethod === 'new') {
      studentsToProcess = (workflowData.newStudents || []).filter(s => s.isValid);
    } else if (workflowData.populationMethod === 'simple') {
      // For simple transitions, students come from class mappings
      studentsToProcess = workflowData.selectedStudents || [];
    } else {
      studentsToProcess = workflowData.selectedStudents || [];
    }

    const decisions = workflowData.studentDecisions || {};
    const studentsByAction = {
      assign: [],
      move: [],
      repeat: [],
      remove: [],
      simpleMove: []
    };

    if (workflowData.populationMethod === 'simple') {
      // For simple method, all students are automatic moves based on class mappings
      const classMappings = workflowData.classMappings || {};
      Object.entries(classMappings).forEach(([sourceClassId, targetClassId]) => {
        // Get students from the source class - this would need to be implemented
        // For now, we'll use a placeholder structure
        studentsByAction.simpleMove.push({
          sourceClassId,
          targetClassId,
          students: [] // Would be populated with actual student data
        });
      });
    } else {
      studentsToProcess.forEach(student => {
        const decision = decisions[student.id];
        if (decision) {
          if (workflowData.populationMethod === 'new') {
            studentsByAction.assign.push({ student, decision });
          } else {
            switch (decision.action) {
              case 'move':
                studentsByAction.move.push({ student, decision });
                break;
              case 'repeat':
                studentsByAction.repeat.push({ student, decision });
                break;
              case 'remove':
                studentsByAction.remove.push({ student, decision });
                break;
            }
          }
        }
      });
    }

    return studentsByAction;
  };

  const getTargetClassName = (targetClassId) => {
    const targetClass = workflowData.createdClasses.find(c => c.id === targetClassId);
    return targetClass ? targetClass.name : 'Unknown Class';
  };

  const getSourceClassName = (sourceClassId) => {
    // Look for source class in the workflow data
    // First check if we have source classes from the StudentSelectionStep
    if (workflowData.sourceClasses) {
      const sourceClass = workflowData.sourceClasses.find(sc => sc.id.toString() === sourceClassId.toString());
      if (sourceClass) {
        return sourceClass.name;
      }
    }
    
    // If not found, return a more user-friendly placeholder
    return `Source Class (ID: ${sourceClassId})`;
  };

  const executeWorkflow = async () => {
    setExecuting(true);
    setExecutionProgress(0);
    setCurrentOperation('Initializing...');

    const results = {
      success: true,
      classes: { created: [], failed: [] },
      students: { 
        assigned: [], 
        moved: [], 
        repeated: [], 
        removed: [], 
        failed: [],
        simpleMoved: [] // For simple transitions
      },
      summary: getSummaryData(),
      executionTime: new Date().toISOString()
    };

    // This will store the mapping from temporary IDs to real database IDs
    let classIdMapping = {};

    try {
      // Step 1: Create Classes (20% of progress)
      setCurrentOperation('Creating classes...');
      setExecutionProgress(10);
      
      const classesToCreate = workflowData.createdClasses.map(classItem => ({
        name: classItem.name,
        grade_level_number: classItem.grade_level_number,
        active: classItem.active,
        trial_class: classItem.trial_class,
        specialed_class: classItem.specialed_class,
        set_class: classItem.set_class,
        teacherId: classItem.teacherId,
        classOwnerId: classItem.classOwnerId
      }));

      const createBulkClasses = workflowData.createBulkClasses;
      if (createBulkClasses && classesToCreate.length > 0) {
        try {
          const createdClasses = await createBulkClasses(classesToCreate);
          
          if (createdClasses && Array.isArray(createdClasses)) {
            results.classes.created = createdClasses;
            
            // Build the ID mapping from temporary workflow IDs to real database IDs
            workflowData.createdClasses.forEach((originalClass, index) => {
              if (createdClasses[index] && createdClasses[index].id) {
                classIdMapping[originalClass.id] = createdClasses[index].id;
                console.log(`Mapped temp ID ${originalClass.id} to real ID ${createdClasses[index].id}`);
              } else {
                console.warn(`Failed to map class at index ${index}: ${originalClass.name}`);
                results.classes.failed.push(originalClass);
              }
            });
            
            console.log('Class ID mapping:', classIdMapping);
          } else {
            console.error('Created classes response is not an array:', createdClasses);
            results.classes.failed = classesToCreate;
            results.success = false;
          }
        } catch (error) {
          console.error('Error creating classes:', error);
          results.classes.failed = classesToCreate;
          results.success = false;
        }
      }

      setExecutionProgress(20);

      // Step 2: Process Students (80% of progress, divided by action type)
      const studentsByAction = getStudentsByAction();
      
      if (workflowData.populationMethod === 'simple') {
        // Handle simple class mapping transitions
        setCurrentOperation('Processing simple class transitions...');
        
        const bulkUpdateStudentClassId = workflowData.bulkUpdateStudentClassId;
        if (bulkUpdateStudentClassId) {
          const classMappings = workflowData.classMappings || {};
          let processedMappings = 0;
          const totalMappings = Object.keys(classMappings).length;
          
          for (const [sourceClassId, targetClassId] of Object.entries(classMappings)) {
            try {
              const realTargetClassId = classIdMapping[targetClassId];
              
              if (!realTargetClassId) {
                console.error(`No real class ID found for temp ID: ${targetClassId}`);
                results.students.failed.push({
                  name: `Students from ${getSourceClassName(sourceClassId)}`,
                  action: 'simpleMove',
                  error: `Target class not created successfully (temp ID: ${targetClassId})`
                });
                continue;
              }
              
              setCurrentOperation(`Moving students from ${getSourceClassName(sourceClassId)} to ${getTargetClassName(targetClassId)}...`);
              
              // This would need to be implemented - get student IDs from source class
              // For now, this is a placeholder
              const sourceStudentIds = []; // Would be populated with actual student IDs from source class
              
              if (sourceStudentIds.length > 0) {
                await bulkUpdateStudentClassId({
                  studentIds: sourceStudentIds,
                  classId: realTargetClassId
                });
                
                results.students.simpleMoved.push({
                  sourceClass: getSourceClassName(sourceClassId),
                  targetClass: getTargetClassName(targetClassId),
                  targetClassId: realTargetClassId,
                  studentCount: sourceStudentIds.length
                });
              }
              
              processedMappings++;
              setExecutionProgress(20 + (processedMappings / totalMappings) * 80);
              
            } catch (error) {
              console.error(`Error processing simple mapping ${sourceClassId} -> ${targetClassId}:`, error);
              results.students.failed.push({
                name: `Students from ${getSourceClassName(sourceClassId)}`,
                action: 'simpleMove',
                error: error.message
              });
            }
          }
        }
      } else {
        // Handle regular transitions (new, complex)
        const totalStudentOperations = studentsByAction.assign.length + 
                                     studentsByAction.move.length + 
                                     studentsByAction.repeat.length + 
                                     studentsByAction.remove.length;
        
        let completedOperations = 0;

        // Process New Student Assignments
        if (studentsByAction.assign.length > 0) {
          setCurrentOperation('Assigning new students to classes...');
          
          const addStudents = workflowData.addStudents;
          if (addStudents) {
            // Group students by target class using the REAL class IDs
            const studentsByClass = {};
            
            studentsByAction.assign.forEach(({ student, decision }) => {
              const tempClassId = decision.targetClassId;
              const realClassId = classIdMapping[tempClassId];
              
              if (!realClassId) {
                console.error(`No real class ID found for temp ID: ${tempClassId}`);
                results.students.failed.push({
                  name: `${student.firstName} ${student.lastName}`.trim(),
                  action: 'assign',
                  error: `Target class not created successfully (temp ID: ${tempClassId})`
                });
                return;
              }
              
              if (!studentsByClass[realClassId]) {
                studentsByClass[realClassId] = [];
              }
              
              studentsByClass[realClassId].push({
                name: `${student.firstName} ${student.lastName}`.trim(),
                gender: student.gender || undefined,
                age: student.age || undefined,
                sen: student.sen,
                esl: student.esl,
                asd: student.asd,
                gdd: student.gdd,
                send: student.send,
                country: student.country || undefined,
                currentGrade: student.currentGrade || undefined,
                isSpecialEd: student.isSpecialEd
              });
            });

            // Add students to each class using real class IDs
            for (const [realClassId, studentsToAdd] of Object.entries(studentsByClass)) {
              try {
                console.log(`Adding ${studentsToAdd.length} students to class ID ${realClassId}`);
                await addStudents(realClassId, studentsToAdd);
                
                studentsToAdd.forEach(studentData => {
                  // Find the original class name for display
                  const originalClass = workflowData.createdClasses.find(c => 
                    classIdMapping[c.id] === realClassId
                  );
                  const className = originalClass ? originalClass.name : `Class ${realClassId}`;
                  
                  results.students.assigned.push({
                    name: studentData.name,
                    className: className,
                    classId: realClassId
                  });
                });
              } catch (error) {
                console.error(`Error adding students to class ${realClassId}:`, error);
                studentsToAdd.forEach(studentData => {
                  results.students.failed.push({
                    name: studentData.name,
                    action: 'assign',
                    error: error.message
                  });
                });
              }
            }
          }

          completedOperations += studentsByAction.assign.length;
          setExecutionProgress(20 + (completedOperations / totalStudentOperations) * 80);
        }

        // Process Student Moves
        if (studentsByAction.move.length > 0) {
          setCurrentOperation('Moving students to new classes...');
          
          const bulkUpdateStudentClassId = workflowData.bulkUpdateStudentClassId;
          if (bulkUpdateStudentClassId) {
            // Group moves by target class using REAL class IDs
            const movesByClass = {};
            
            studentsByAction.move.forEach(({ student, decision }) => {
              const tempClassId = decision.targetClassId;
              const realClassId = classIdMapping[tempClassId];
              
              if (!realClassId) {
                console.error(`No real class ID found for temp ID: ${tempClassId}`);
                results.students.failed.push({
                  name: student.firstName && student.lastName 
                    ? `${student.firstName} ${student.lastName}` 
                    : student.name,
                  action: 'move',
                  error: `Target class not created successfully (temp ID: ${tempClassId})`
                });
                return;
              }
              
              if (!movesByClass[realClassId]) {
                movesByClass[realClassId] = [];
              }
              movesByClass[realClassId].push(student);
            });

            // Execute moves for each class using real class IDs
            for (const [realClassId, studentsToMove] of Object.entries(movesByClass)) {
              try {
                console.log(`Moving ${studentsToMove.length} students to class ID ${realClassId}`);
                await bulkUpdateStudentClassId({
                  studentIds: studentsToMove.map(s => s.id),
                  classId: realClassId
                });
                
                studentsToMove.forEach(student => {
                  // Find the original class name for display
                  const originalClass = workflowData.createdClasses.find(c => 
                    classIdMapping[c.id] === realClassId
                  );
                  const className = originalClass ? originalClass.name : `Class ${realClassId}`;
                  
                  results.students.moved.push({
                    name: student.firstName && student.lastName 
                      ? `${student.firstName} ${student.lastName}` 
                      : student.name,
                    fromClass: student.className,
                    toClass: className,
                    classId: realClassId
                  });
                });
              } catch (error) {
                console.error(`Error moving students to class ${realClassId}:`, error);
                studentsToMove.forEach(student => {
                  results.students.failed.push({
                    name: student.firstName && student.lastName 
                      ? `${student.firstName} ${student.lastName}` 
                      : student.name,
                    action: 'move',
                    error: error.message
                  });
                });
              }
            }
          }

          completedOperations += studentsByAction.move.length;
          setExecutionProgress(20 + (completedOperations / totalStudentOperations) * 80);
        }

        // Process Student Repeats
        if (studentsByAction.repeat.length > 0) {
          setCurrentOperation('Marking students as repeating...');
          
          const markStudentAsRepeat = workflowData.markStudentAsRepeat;
          if (markStudentAsRepeat) {
            for (const { student, decision } of studentsByAction.repeat) {
              try {
                await markStudentAsRepeat(
                  student.id,
                  decision.repeatYear,
                  workflowData.teacherid?.toString() || "workflow",
                  workflowData.teacherName || "Class Transition Workflow",
                  decision.reason
                );
                
                results.students.repeated.push({
                  name: student.firstName && student.lastName 
                    ? `${student.firstName} ${student.lastName}` 
                    : student.name,
                  className: student.className,
                  repeatYear: decision.repeatYear,
                  reason: decision.reason
                });
              } catch (error) {
                console.error(`Error marking student ${student.id} as repeating:`, error);
                results.students.failed.push({
                  name: student.firstName && student.lastName 
                    ? `${student.firstName} ${student.lastName}` 
                    : student.name,
                  action: 'repeat',
                  error: error.message
                });
              }

              completedOperations++;
              setExecutionProgress(20 + (completedOperations / totalStudentOperations) * 80);
            }
          }
        }

        // Process Student Removals
        if (studentsByAction.remove.length > 0) {
          setCurrentOperation('Removing students from system...');
          
          const batchDeleteStudents = workflowData.batchDeleteStudents;
          if (batchDeleteStudents) {
            try {
              const response = await batchDeleteStudents({
                studentIds: studentsByAction.remove.map(({ student }) => student.id),
                deletionReason: "Class Transition Workflow - Bulk Removal",
                deletedByTeacherId: workflowData.teacherid?.toString() || "workflow",
                deletedByTeacherName: workflowData.teacherName || "Class Transition Workflow"
              });

              studentsByAction.remove.forEach(({ student, decision }) => {
                results.students.removed.push({
                  name: student.firstName && student.lastName 
                    ? `${student.firstName} ${student.lastName}` 
                    : student.name,
                  className: student.className,
                  reason: decision.reason
                });
              });
            } catch (error) {
              console.error('Error removing students:', error);
              studentsByAction.remove.forEach(({ student }) => {
                results.students.failed.push({
                  name: student.firstName && student.lastName 
                    ? `${student.firstName} ${student.lastName}` 
                    : student.name,
                  action: 'remove',
                  error: error.message
                });
              });
            }
          }

          completedOperations += studentsByAction.remove.length;
        }
      }

      setExecutionProgress(100);
      setCurrentOperation('Execution complete!');
      
    } catch (error) {
      console.error('Workflow execution error:', error);
      results.success = false;
      results.error = error.message;
    }

    setExecutionResults(results);
    setExecuting(false);
  };

  const downloadSummary = () => {
    if (!executionResults) return;

    const summaryText = generateSummaryText(executionResults);
    const blob = new Blob([summaryText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class-transition-summary-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateSummaryText = (results) => {
    const summary = results.summary;
    const timestamp = new Date(results.executionTime).toLocaleString();
    
    let text = `CLASS TRANSITION WORKFLOW SUMMARY\n`;
    text += `Executed: ${timestamp}\n`;
    text += `Population Method: ${workflowData.populationMethod}\n`;
    text += `Overall Status: ${results.success ? 'SUCCESS' : 'FAILED'}\n\n`;
    
    text += `CLASSES CREATED:\n`;
    text += `- Total: ${summary.totalClasses}\n`;
    text += `- Successful: ${results.classes.created.length}\n`;
    text += `- Failed: ${results.classes.failed.length}\n\n`;
    
    if (results.classes.created.length > 0) {
      text += `Created Classes:\n`;
      results.classes.created.forEach(cls => {
        text += `  - ${cls.name} (ID: ${cls.id})\n`;
      });
      text += `\n`;
    }
    
    text += `STUDENTS PROCESSED:\n`;
    text += `- Total: ${summary.totalStudents}\n`;
    
    if (workflowData.populationMethod === 'new') {
      text += `- Assigned: ${results.students.assigned.length}\n`;
      if (results.students.assigned.length > 0) {
        text += `\nAssigned Students:\n`;
        results.students.assigned.forEach(student => {
          text += `  - ${student.name} → ${student.className} (ID: ${student.classId})\n`;
        });
      }
    } else if (workflowData.populationMethod === 'simple') {
      text += `- Class Mappings: ${summary.simpleMappingCount}\n`;
      text += `- Simple Moves: ${results.students.simpleMoved.length}\n`;
      if (results.students.simpleMoved.length > 0) {
        text += `\nSimple Class Transitions:\n`;
        results.students.simpleMoved.forEach(move => {
          text += `  - ${move.sourceClass} → ${move.targetClass} (${move.studentCount} students, ID: ${move.targetClassId})\n`;
        });
      }
    } else {
      text += `- Moved: ${results.students.moved.length}\n`;
      text += `- Repeated: ${results.students.repeated.length}\n`;
      text += `- Removed: ${results.students.removed.length}\n`;
      
      if (results.students.moved.length > 0) {
        text += `\nMoved Students:\n`;
        results.students.moved.forEach(student => {
          text += `  - ${student.name}: ${student.fromClass} → ${student.toClass} (ID: ${student.classId})\n`;
        });
      }
      
      if (results.students.repeated.length > 0) {
        text += `\nRepeating Students:\n`;
        results.students.repeated.forEach(student => {
          text += `  - ${student.name} (${student.className}): ${student.repeatYear} - ${student.reason}\n`;
        });
      }
      
      if (results.students.removed.length > 0) {
        text += `\nRemoved Students:\n`;
        results.students.removed.forEach(student => {
          text += `  - ${student.name} (${student.className}): ${student.reason}\n`;
        });
      }
    }
    
    text += `\n- Failed: ${results.students.failed.length}\n`;
    if (results.students.failed.length > 0) {
      text += `\nFailed Operations:\n`;
      results.students.failed.forEach(failure => {
        text += `  - ${failure.name} (${failure.action}): ${failure.error}\n`;
      });
    }
    
    return text;
  };

  const summary = getSummaryData();
  const studentsByAction = getStudentsByAction();

  // If execution is complete, show results
  if (executionResults) {
    return (
      <div>
        <h5>Execution Results</h5>
        <p className="text-muted">Class transition workflow has been executed.</p>
        
        {executionResults.success ? (
          <Alert variant="success">
            <FaCheck className="me-2" />
            Workflow executed successfully!
          </Alert>
        ) : (
          <Alert variant="danger">
            <FaExclamationTriangle className="me-2" />
            Workflow execution completed with errors. Please review the summary below.
          </Alert>
        )}

        {/* Results Summary */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Classes Created</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between">
                  <span>Total:</span>
                  <Badge bg="primary">{summary.totalClasses}</Badge>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Successful:</span>
                  <Badge bg="success">{executionResults.classes.created.length}</Badge>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Failed:</span>
                  <Badge bg="danger">{executionResults.classes.failed.length}</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Students Processed</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between">
                  <span>Total:</span>
                  <Badge bg="primary">{summary.totalStudents}</Badge>
                </div>
                {workflowData.populationMethod === 'new' ? (
                  <div className="d-flex justify-content-between">
                    <span>Assigned:</span>
                    <Badge bg="success">{executionResults.students.assigned.length}</Badge>
                  </div>
                ) : workflowData.populationMethod === 'simple' ? (
                  <div className="d-flex justify-content-between">
                    <span>Class Transitions:</span>
                    <Badge bg="info">{executionResults.students.simpleMoved.length}</Badge>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between">
                      <span>Moved:</span>
                      <Badge bg="info">{executionResults.students.moved.length}</Badge>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Repeated:</span>
                      <Badge bg="warning">{executionResults.students.repeated.length}</Badge>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Removed:</span>
                      <Badge bg="danger">{executionResults.students.removed.length}</Badge>
                    </div>
                  </>
                )}
                <div className="d-flex justify-content-between">
                  <span>Failed:</span>
                  <Badge bg="danger">{executionResults.students.failed.length}</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Download Summary */}
        <div className="text-center mb-4">
          <Button variant="success" onClick={downloadSummary} size="lg">
            <FaDownload className="me-2" />
            Download Summary Report
          </Button>
        </div>

        {/* Complete Workflow */}
        <div className="text-center">
          <Button variant="primary" onClick={onComplete} size="lg">
            <FaCheck className="me-2" />
            Complete Workflow
          </Button>
        </div>
      </div>
    );
  }

  // If executing, show progress
  if (executing) {
    return (
      <div>
        <h5>Executing Workflow</h5>
        <p className="text-muted">Please wait while the class transition is being processed...</p>
        
        <div className="mb-4">
          <ProgressBar 
            now={executionProgress} 
            label={`${Math.round(executionProgress)}%`}
            animated 
            striped
          />
          <div className="mt-2 text-center">
            <FaSpinner className="fa-spin me-2" />
            {currentOperation}
          </div>
        </div>
      </div>
    );
  }

  // Default view - show summary and execute button
  return (
    <div>
      <h5>Review and Execute</h5>
      <p className="text-muted">Review all changes before executing the class transition workflow.</p>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{summary.totalClasses}</h3>
              <small className="text-muted">Classes to Create</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info">{summary.totalStudents}</h3>
              <small className="text-muted">
                {workflowData.populationMethod === 'simple' ? 'Class Mappings' : 'Students to Process'}
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">
                {workflowData.populationMethod === 'new' ? summary.assignCount : 
                 workflowData.populationMethod === 'simple' ? summary.simpleMappingCount :
                 summary.moveCount + summary.repeatCount + summary.removeCount}
              </h3>
              <small className="text-muted">
                {workflowData.populationMethod === 'simple' ? 'Total Mappings' : 'Total Decisions'}
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Action Breakdown for Simple Transitions */}
      {workflowData.populationMethod === 'simple' && (
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Class Mapping Summary</h6>
          </Card.Header>
          <Card.Body>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {Object.entries(workflowData.classMappings || {}).map(([sourceClassId, targetClassId]) => (
                <div key={`${sourceClassId}-${targetClassId}`} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <span>{getSourceClassName(sourceClassId)}</span>
                  <FaArrowRight className="text-primary mx-2" />
                  <span>{getTargetClassName(targetClassId)}</span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Action Breakdown for Complex Transitions */}
      {workflowData.populationMethod === 'complex' && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center border-info">
              <Card.Body>
                <h4 className="text-info">{summary.moveCount}</h4>
                <small className="text-muted">Students to Move</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-warning">
              <Card.Body>
                <h4 className="text-warning">{summary.repeatCount}</h4>
                <small className="text-muted">Students to Repeat</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-danger">
              <Card.Body>
                <h4 className="text-danger">{summary.removeCount}</h4>
                <small className="text-muted">Students to Remove</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center border-success">
              <Card.Body>
                <h4 className="text-success">{summary.assignCount}</h4>
                <small className="text-muted">Students to Assign</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Classes to Create */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0">Classes to Create ({summary.totalClasses})</h6>
        </Card.Header>
        <Card.Body>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <Table striped hover size="sm">
              <thead>
                <tr>
                  <th>Class Name</th>
                  <th>Grade</th>
                  <th>Teacher</th>
                  <th>Owner</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {workflowData.createdClasses.map(classItem => (
                  <tr key={classItem.id}>
                    <td>{classItem.name}</td>
                    <td>{classItem.grade_level_number}</td>
                    <td>{classItem.teacherName || 'N/A'}</td>
                    <td>{classItem.ownerName || 'N/A'}</td>
                    <td>
                      <Badge bg={classItem.active ? 'success' : 'secondary'}>
                        {classItem.active ? 'Active' : 'Inactive'}
                      </Badge>
                      {classItem.trial_class && <Badge bg="warning" className="ms-1">Trial</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Student Actions Preview - Only for Complex Transitions */}
      {workflowData.populationMethod === 'complex' && (
        <>
          {studentsByAction.move.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0 text-info">Students to Move ({studentsByAction.move.length})</h6>
              </Card.Header>
              <Card.Body>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {studentsByAction.move.map(({ student, decision }, index) => (
                    <div key={index} className="d-flex justify-content-between py-1 border-bottom">
                      <span>{student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : student.name}</span>
                      <span className="text-muted">
                        {student.className} → {getTargetClassName(decision.targetClassId)}
                      </span>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {studentsByAction.repeat.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0 text-warning">Students to Repeat ({studentsByAction.repeat.length})</h6>
              </Card.Header>
              <Card.Body>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {studentsByAction.repeat.map(({ student, decision }, index) => (
                    <div key={index} className="py-1 border-bottom">
                      <div className="d-flex justify-content-between">
                        <span>{student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : student.name}</span>
                        <span className="text-muted">{decision.repeatYear}</span>
                      </div>
                      <small className="text-muted">{decision.reason}</small>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {studentsByAction.remove.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0 text-danger">Students to Remove ({studentsByAction.remove.length})</h6>
              </Card.Header>
              <Card.Body>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {studentsByAction.remove.map(({ student, decision }, index) => (
                    <div key={index} className="py-1 border-bottom">
                      <div className="d-flex justify-content-between">
                        <span>{student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : student.name}</span>
                        <span className="text-muted">{student.className}</span>
                      </div>
                      <small className="text-muted">{decision.reason}</small>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* Student Assignments Preview - Only for New Student Method */}
      {workflowData.populationMethod === 'new' && studentsByAction.assign.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            <h6 className="mb-0 text-success">New Students to Assign ({studentsByAction.assign.length})</h6>
          </Card.Header>
          <Card.Body>
            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {studentsByAction.assign.map(({ student, decision }, index) => (
                <div key={index} className="d-flex justify-content-between py-1 border-bottom">
                  <span>{student.displayName}</span>
                  <span className="text-muted">
                    → {getTargetClassName(decision.targetClassId)}
                  </span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Execute Button */}
      <div className="text-center">
        <Button 
          variant="success" 
          size="lg" 
          onClick={() => setShowConfirmModal(true)}
          disabled={summary.totalClasses === 0 && summary.totalStudents === 0}
        >
          <FaPlay className="me-2" />
          Execute Class Transition
        </Button>
      </div>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>
            <FaExclamationTriangle className="me-2" />
            Confirm Execution
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to execute this class transition workflow?</p>
          <p className="text-muted">This will:</p>
          <ul className="text-muted">
            <li>Create {summary.totalClasses} new classes</li>
            {workflowData.populationMethod === 'simple' && (
              <li>Process {summary.simpleMappingCount} class mappings with automatic student transitions</li>
            )}
            {workflowData.populationMethod === 'new' && (
              <li>Assign {summary.assignCount} new students to classes</li>
            )}
            {workflowData.populationMethod === 'complex' && (
              <>
                <li>Process {summary.totalStudents} students</li>
                {summary.moveCount > 0 && <li>Move {summary.moveCount} students to new classes</li>}
                {summary.repeatCount > 0 && <li>Mark {summary.repeatCount} students as repeating</li>}
                {summary.removeCount > 0 && <li>Remove {summary.removeCount} students from the system</li>}
              </>
            )}
          </ul>
          <Alert variant="warning">
            <strong>This action cannot be undone.</strong> Please ensure all information is correct before proceeding.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            <FaTimes className="me-2" />
            Cancel
          </Button>
          <Button variant="danger" onClick={() => {
            setShowConfirmModal(false);
            executeWorkflow();
          }}>
            <FaPlay className="me-2" />
            Execute Now
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ConfirmationStep;